import React from 'react';
import { format } from 'date-fns';
import { MarkdownRenderer } from '../common';
import { calculatePercentOriginal, formatPercentOriginal, isEdited } from '../../utils/textSimilarity';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3000';

const getMediaUrl = (url) => {
  if (!url) return null;
  if (url.startsWith('/uploads/')) {
    return `${API_URL}${url}`;
  }
  return url;
};

// Helper to generate passage header display text based on available data
// Returns null for title/subtitle when not provided (no defaults)
const getPassageDisplayText = (passage) => {
  const hasTitle = passage.title && passage.title.trim();
  const hasDescription = passage.description && passage.description.trim();

  if (hasTitle && hasDescription) {
    return { title: passage.title, subtitle: passage.description };
  } else if (hasTitle) {
    return { title: passage.title, subtitle: null };
  } else if (hasDescription) {
    return { title: null, subtitle: passage.description };
  }
  return { title: null, subtitle: null };
};

const AspectBadges = ({ aspects, show = true }) => {
  if (!show || !aspects || aspects.length === 0) return null;

  return (
    <div className="flex flex-wrap gap-1 mt-2 mb-1">
      {aspects.map((aspect, idx) => (
        <span
          key={idx}
          className="text-xs px-2 py-0.5 rounded-full bg-primary-100 text-primary-700"
          title={aspect.aspectName}
        >
          {aspect.aspectName}
        </span>
      ))}
    </div>
  );
};

const Attribution = ({ authorDisplayName, show = true }) => {
  if (!show || !authorDisplayName) return null;

  return (
    <div className="text-xs text-gray-500 mb-1">
      — {authorDisplayName}
    </div>
  );
};

const BlockContent = ({ block, showAspects = true, showAttribution = true }) => {
  // GM text content types
  if (block.type === 'gm_prose' || block.type === 'gm_text') {
    return (
      <MarkdownRenderer content={block.content} />
    );
  }

  if (block.type === 'gm_transition') {
    return (
      <div className="text-gray-600 italic">
        <MarkdownRenderer content={block.content} />
      </div>
    );
  }

  // GM media content types
  if (block.type === 'gm_image') {
    return (
      <figure className="my-4">
        {block.mediaUrl && (
          <img
            src={getMediaUrl(block.mediaUrl)}
            alt="GM image"
            className="rounded-lg w-full max-w-md h-auto object-contain"
          />
        )}
      </figure>
    );
  }

  if (block.type === 'gm_audio') {
    return (
      <div className="my-4">
        {block.mediaUrl && (
          <audio controls className="w-full">
            <source src={getMediaUrl(block.mediaUrl)} />
            Your browser does not support audio playback.
          </audio>
        )}
      </div>
    );
  }

  // Player content - check contentType
  if (block.type === 'player_content') {
    const aspects = block.referencedAspects;
    const authorDisplayName = block.authorDisplayName;
    const hasBeenEdited = block.originalContent && isEdited(block.originalContent, block.content);
    const percentOriginal = hasBeenEdited ? calculatePercentOriginal(block.originalContent, block.content) : null;

    if (block.contentType === 'prose') {
      return (
        <div>
          <Attribution authorDisplayName={authorDisplayName} show={showAttribution} />
          {hasBeenEdited && showAttribution && (
            <span className="inline-block text-xs px-1.5 py-0.5 mb-1 rounded bg-amber-100 text-amber-700">
              {formatPercentOriginal(percentOriginal) || 'edited by GM'}
            </span>
          )}
          <AspectBadges aspects={aspects} show={showAspects} />
          <MarkdownRenderer content={block.content} />
        </div>
      );
    }

    if (block.contentType === 'image') {
      return (
        <figure className="my-4">
          <Attribution authorDisplayName={authorDisplayName} show={showAttribution} />
          <AspectBadges aspects={aspects} show={showAspects} />
          {block.mediaUrl && (
            <img
              src={getMediaUrl(block.mediaUrl)}
              alt={block.content || 'Player image'}
              className="rounded-lg w-full max-w-md h-auto object-contain"
            />
          )}
        </figure>
      );
    }

    if (block.contentType === 'audio') {
      return (
        <div className="my-4">
          <Attribution authorDisplayName={authorDisplayName} show={showAttribution} />
          <AspectBadges aspects={aspects} show={showAspects} />
          {block.mediaUrl && (
            <audio controls className="w-full">
              <source src={getMediaUrl(block.mediaUrl)} />
              Your browser does not support audio playback.
            </audio>
          )}
        </div>
      );
    }
  }

  // Fallback
  return null;
};

export const PassageDisplay = ({
  passage,
  showMeta = true,
  showPrompt = true,
  showWinningDetail = true,
  showAspects = true,
  showAttribution = true
}) => {
  if (!passage) return null;

  const { content, scene, number, publishedAt, prompt, winningDetail } = passage;

  return (
    <article className="mb-8">
      {/* Header - only show if there's title, subtitle, or date */}
      {showMeta && (() => {
        const { title, subtitle } = getPassageDisplayText(passage);
        const hasHeader = title || subtitle || publishedAt;
        if (!hasHeader) return null;

        return (
          <header className="mb-4 pb-4 border-b">
            <div className="flex items-center justify-between">
              <div>
                {title && (
                  <h3 className="text-lg font-semibold">{title}</h3>
                )}
                {subtitle && (
                  <p className="text-sm text-gray-500">{subtitle}</p>
                )}
              </div>
              {publishedAt && (
                <span className="text-sm text-gray-400">
                  {format(new Date(publishedAt), 'MMM d, yyyy')}
                </span>
              )}
            </div>
          </header>
        );
      })()}

      {/* GM Prompt as Epigraph */}
      {showPrompt && prompt?.content && (
        <blockquote className="mb-6 pl-4 border-l-4 border-primary-300 bg-primary-50/50 py-3 pr-4 rounded-r-lg">
          <div className="text-gray-700 italic">
            <MarkdownRenderer content={prompt.content} />
          </div>
          {prompt.mediaUrl && prompt.mediaType === 'image' && (
            <img
              src={getMediaUrl(prompt.mediaUrl)}
              alt="Prompt illustration"
              className="mt-3 rounded-lg w-full max-w-sm h-auto object-contain"
            />
          )}
          {prompt.mediaUrl && prompt.mediaType === 'audio' && (
            <audio controls className="mt-3 w-full">
              <source src={getMediaUrl(prompt.mediaUrl)} />
            </audio>
          )}
          <footer className="mt-2 text-xs text-gray-500">— GM Prompt</footer>
        </blockquote>
      )}

      {/* Winning Detail */}
      {showWinningDetail && winningDetail && (
        <div className="mb-6 pl-4 border-l-4 border-amber-400 bg-amber-50/50 py-3 pr-4 rounded-r-lg">
          <p className="text-amber-900 font-medium">"{winningDetail.content}"</p>
          <footer className="mt-2 text-xs text-amber-700">
            — Winning Detail by {winningDetail.authorDisplayName} ({winningDetail.totalBid} bid{winningDetail.totalBid !== 1 ? 's' : ''})
          </footer>
        </div>
      )}

      {/* Content */}
      <div className="prose prose-sm max-w-none overflow-hidden">
        {content?.map((block, index) => (
          <div key={block.id || index} className="mb-4">
            <BlockContent
              block={block}
              showAspects={showAspects}
              showAttribution={showAttribution}
            />
          </div>
        ))}
      </div>
    </article>
  );
};
